// The audio engine (decision 0045; card engine/docs/composer/cards/audio.md): one context, the mixer, a bank of
// synthesised cues with variants, positional one-shots through a voice pool with muffling behind walls (an occlusion
// function the game passes in), virtual loop emitters, ambience beds and spots by area and time, the music director,
// babble voices with dialogue-first ducking, and a caption event per important sound. Genre-neutral: every cue, level,
// mood, bed and voice comes from the game.
//
// One AudioContext: the game creates it on the first gesture and `attach`es it; a second context is refused and
// reported (`problems()`), never used. Before a context exists nothing sounds, but plays are still counted and
// important sounds still send their captions (a caption is for the player who can't hear it). Works on an
// OfflineAudioContext too: `clock` drives time in a pre-scheduled render (`renderOffline`).
//
// The music (the music pass, 2026-10-19): instruments are multisamples (a recipe rendered at several roots; a note
// plays the nearest root, shifted at most a few semitones), sustained ones loop their middle and release on the note's
// end with their own time constant; notes spread across the stereo field by pitch; the music bus has a space (a
// generated room on a convolver and a tempo-synced echo) the director's tempo follows.
import { bedTargets, SpotScheduler, type AmbienceState, type AmbienceTable } from "./ambience";
import { babblePlan, renderSyllable, type BabbleVoice } from "./babble";
import { directionOf, type CaptionEvent, type Vec3 } from "./captions";
import { dbToGain, mulberry32, sustainLoop, type Rng } from "./dsp";
import { Mixer, type BusId, type DuckLevels, type MasterOptions, type MusicSpace } from "./mixer";
import { MusicDirector, type MusicLayer, type MusicTable, type NoteEvent } from "./music";
import { LoopField, VoicePool } from "./pool";
import { renderSound, type Rendered, type SoundRecipe } from "./recipes";
import { makeIR, type IrSpec } from "./reverb";

/** A cue: its recipe, bus and level, how it varies, and whether it is important (then it needs a caption). */
export interface SoundDef {
  recipe: SoundRecipe;
  bus: BusId;
  /** dB (default 0). */
  gain?: number;
  /** Important sounds send a caption event every time they play (decision 0063). */
  important?: boolean;
  /** The caption's string id (the game's table). */
  caption?: string | null;
  /** Higher steals lower in the voice pool (default 1). */
  priority?: number;
  /** Positional sounds past this are culled (m, default 40). */
  range?: number;
  /** Random pitch per play, ± semitones (default 0.6). */
  pitch?: number;
  /** Random level per play, ± dB (default 1.2). */
  vol?: number;
}

/** The positional layer's numbers. */
export interface PoolOptions {
  /** One-shot voices (10 to 24 by tier). */
  voices: number;
  /** Loop emitters with a live graph at once. */
  loops: number;
  /** HRTF panning within this distance (m); equal-power beyond (0: never, the phone's cheaper panning). */
  hrtfWithin: number;
  /** The panner's inverse-distance reference (m) and rolloff. */
  refDistance: number;
  rolloff: number;
  /** Air absorption: the low-pass falls as 20 kHz × e^(−d / air), not under 1.4 kHz. */
  air: number;
  /** Behind a wall (occlusion 1): the low-pass cutoff (Hz) and the gain (linear) it ends at. */
  muffle: { cutoff: number; gain: number };
}

/** An instrument for the music: a recipe that sounds at the MIDI note `ref`, rendered at each of `roots` (default
 *  `[ref]`: one sample) and replayed at other pitches from the nearest root. */
export interface Instrument {
  recipe: SoundRecipe;
  ref: number;
  /** The multisample's roots (MIDI): roots every 4 to 6 semitones keep every shift small. */
  roots?: number[];
  /** A sustained instrument loops this stretch (seconds into the render) for as long as a note lasts. */
  sustain?: [number, number];
  /** The release's time constant after a note's end (s; default 0.12). */
  release?: number;
  /** Level (dB; default 0). */
  gain?: number;
  /** Stereo spread by pitch: pan = (midi − 66) / 30 × spread, within ±0.8 (default 0: centred). */
  spread?: number;
  /** Sends into the music's space (linear): the reverb (default the space's `reverb.send`) and the echo (default 0). */
  reverb?: number;
  echo?: number;
}

/** Planted defect beside what it breaks: the multisample check must go red with it on. */
export const INSTRUMENT_FAULTS = {
  /** Every note plays the instrument's first root, shifted the whole way: the multisample check goes red. */
  firstRootOnly: false,
};

/** Everything the game passes. */
export interface AudioOptions {
  sounds: Record<string, SoundDef>;
  duck: DuckLevels;
  master: MasterOptions;
  pool: PoolOptions;
  music?: {
    table: MusicTable;
    instruments: Record<string, Instrument>;
    mood?: string;
    lookahead?: number;
    /** The music bus's space: the room (rendered at the context's rate on attach) and the sends and echo. */
    space?: MusicSpace & { ir: IrSpec };
  };
  ambience?: AmbienceTable;
  /** Babble voices by id, and the level of a line (dB). */
  voices?: Record<string, BabbleVoice>;
  babbleGain?: number;
  /** How much a wall stands between two points (0 open … 1 fully behind a wall); the game's ray check. */
  occlusion?: (from: Vec3, to: Vec3) => number;
  seed?: number;
  /** The engine's time (default the context's `currentTime`, else the page's clock in seconds). */
  clock?: () => number;
  /** Don't render a loop's or a bed's cue, or the music's instruments, on demand (a long render is a hitch): wait for
   *  `warmStep` to reach them (the music starts on the first bar line after its instruments are ready). */
  deferLoops?: boolean;
}

/** How a sound is played. */
export interface PlayOptions {
  /** World position (null or absent: on the listener, no pan). */
  pos?: Vec3 | null;
  /** Extra level (dB). */
  gain?: number;
  /** When (context seconds; default now). */
  at?: number;
  /** A playback rate on top of the jitter. */
  rate?: number;
  /** Force a variant (tests). */
  variant?: number;
  /** Flag this play important (a game event may), on top of the cue's own flag. */
  important?: boolean;
}

/** What a play did. */
export interface PlayResult {
  played: boolean;
  /** Why it didn't sound: no context yet, an unknown cue, out of range, or no voice free. */
  reason?: "locked" | "unknown" | "range" | "culled";
  caption: CaptionEvent | null;
  /** The pool slot (positional sounds). */
  voice: number | null;
}

/** A babbled line. */
export interface SayLine {
  speaker: string;
  /** The voice row (absent: the speaker's id). */
  voice?: string;
  text: string;
  /** Seconds the line is shown (the babble fits inside it). */
  seconds?: number;
  pos?: Vec3 | null;
}

/** The engine's numbers. */
export interface AudioStats {
  contexts: number;
  state: string;
  plays: Record<string, number>;
  captions: Record<string, number>;
  /** Plays flagged important, and those that had no caption to send (each should be 0). */
  important: Record<string, number>;
  uncaptioned: Record<string, number>;
  dropped: number;
  voices: { size: number; busy: number; peakBusy: number; granted: number; stolen: number; culled: number };
  loops: { emitters: number; built: number; max: number; peakBuilt: number; builds: number; releases: number; handoffs: number };
  muffled: number;
  ducks: number;
  babble: { lines: number; syllables: number };
  music: ReturnType<MusicDirector["state"]> | null;
  ambience: { state: AmbienceState; beds: Record<string, number>; spots: Record<string, number> };
  rendered: { cues: number; ms: number; samples: number; instMs: number; irMs: number };
}

/** The engine. */
export interface Audio {
  /** Use this context (create it on the first gesture). A second, different context is refused. */
  attach(ctx: BaseAudioContext, destination?: AudioNode): boolean;
  readonly context: BaseAudioContext | null;
  readonly mixer: Mixer | null;
  /** The engine's time (seconds): the clock its music and sounds are scheduled on (the options' `clock`, else the
   *  context's `currentTime`, else the page's clock before a context exists). A music source driven from outside the
   *  engine (an adaptive-music feature over `music.director()`) passes this as its `now`, so its requests land exactly
   *  as the engine's own `music.mood`, `alert` and `victory` would. */
  now(): number;
  play(cue: string, o?: PlayOptions): PlayResult;
  /** The listener's position and facing (each frame). */
  listener(pos: Vec3, forward: Vec3): void;
  /** A looping emitter (always a handle; a graph only when near). */
  loop(id: string, cue: string, pos: Vec3, o?: { range?: number; gain?: number }): void;
  removeLoop(id: string): void;
  /** The ambience's area, time mark and rain (0..1). */
  ambience(s: Partial<AmbienceState>): void;
  music: {
    mood(id: string): void;
    alert(on: boolean): void;
    victory(): void;
    director(): MusicDirector | null;
    /** Play one note on a music instrument (a sting, a test), through the same chain as the director's. */
    note(e: Omit<NoteEvent, "layer"> & { layer?: MusicLayer }): void;
  };
  /** Babble a line on the voice bus and duck the rest under it. */
  say(line: SayLine): { syllables: number; seconds: number };
  /** Duck music, effects and beds from now for `seconds`. */
  duck(seconds: number): void;
  volume(bus: BusId | "master", v: number): void;
  /** Hear every caption event. */
  onCaption(fn: (c: CaptionEvent) => void): () => void;
  /** Per frame: music scheduling, loops, spots. */
  update(): void;
  /** Render every cue now (a warm-up, or a test). */
  warm(ids?: readonly string[]): number;
  /** Render cues and instrument samples not yet rendered, in `order` (then the rest; an instrument is named
   *  `inst:<id>`, and unnamed instruments come after the named cues), until `budgetMs` is spent (at least one sample):
   *  a warm-up spread over frames. Returns how many are left. */
  warmStep(budgetMs: number, order?: readonly string[]): number;
  /** The live output's peak over its last block (linear), from an analyser after the ceiling. */
  meter(): number;
  stats(): AudioStats;
  problems(): string[];
  dispose(): void;
}

/** Problems with the game's cue table: important cues without a caption, unknown buses, empty recipes. */
export function checkSounds(sounds: Record<string, SoundDef>): string[] {
  const out: string[] = [];
  for (const [id, s] of Object.entries(sounds)) {
    if (s.important && !s.caption) out.push(`${id}: important, but no caption`);
    if (!["music", "effects", "ambience", "voice", "ui"].includes(s.bus)) out.push(`${id}: unknown bus "${s.bus}"`);
    if (!s.recipe?.layers?.length) out.push(`${id}: no layers`);
  }
  return out;
}

const semis = (s: number): number => Math.pow(2, s / 12);

/** Build the engine (no context yet: `attach` one). */
export function createAudio(o: AudioOptions): Audio {
  let ctx: BaseAudioContext | null = null;
  let mixer: Mixer | null = null;
  let analyser: AnalyserNode | null = null;
  const problems: string[] = [];
  const rng: Rng = mulberry32(o.seed ?? 7);
  const pool = new VoicePool(o.pool.voices);
  const field = new LoopField({ max: o.pool.loops });
  const plays: Record<string, number> = {};
  const captions: Record<string, number> = {};
  const importantPlays: Record<string, number> = {};
  const uncaptioned: Record<string, number> = {};
  const captionFns = new Set<(c: CaptionEvent) => void>();
  const lastVariant = new Map<string, number>();
  const bank = new Map<string, Rendered[]>();
  const buffers = new Map<string, AudioBuffer[]>();
  /** Each instrument's rendered roots (MIDI → buffer), and the loop points used. */
  const instBuffers = new Map<string, Map<number, AudioBuffer>>();
  const instLoops = new Map<string, [number, number]>();
  let instMs = 0;
  let instSamples = 0;
  let irMs = 0;
  /** Base-layer music notes still sounding (a mood change releases them). */
  const held = new Set<{ src: AudioBufferSourceNode; g: GainNode; end: number; release: number }>();
  const syllables = new Map<string, AudioBuffer>();
  const active = new Map<number, { src: AudioBufferSourceNode; gain: GainNode; serial: number }>();
  const loops = new Map<string, { cue: string; pos: Vec3; range: number; gain: number; graph: { src: AudioBufferSourceNode; gain: GainNode; filter: BiquadFilterNode; panner: PannerNode } | null }>();
  const beds = new Map<string, { src: AudioBufferSourceNode; gain: GainNode }>();
  let amb: AmbienceState = { area: null, time: null, rain: 0 };
  let bedLevels: Record<string, number> = {};
  const spots = o.ambience ? new SpotScheduler(o.ambience.spots, (o.seed ?? 7) + 101) : null;
  let lis: { pos: Vec3; fwd: Vec3 } = { pos: [0, 0, 0], fwd: [0, 0, -1] };
  let dropped = 0;
  let muffled = 0;
  let babble = { lines: 0, syllables: 0 };
  let renderMs = 0;
  let lastCull = -Infinity;
  const volumes: Partial<Record<BusId | "master", number>> = {};
  const t0 = typeof performance !== "undefined" ? performance.now() : Date.now();
  const now = (): number => (o.clock ? o.clock() : ctx ? ctx.currentTime : ((typeof performance !== "undefined" ? performance.now() : Date.now()) - t0) / 1000);

  const director = o.music
    ? new MusicDirector(
        o.music.table,
        {
          note: (e) => playNote(e),
          fade: (layer, target, start, seconds) => mixer?.fadeLayer(layer, target, start, seconds),
          tempo: (bpm, at) => mixer?.tempo(bpm, at),
          cut: (at) => cutHeld(at),
        },
        { seed: (o.seed ?? 7) + 31, ...(o.music.mood ? { mood: o.music.mood } : {}) },
      )
    : null;

  const rendered = (id: string): Rendered[] => {
    let r = bank.get(id);
    if (r) return r;
    const def = o.sounds[id];
    if (!def) return [];
    const t = typeof performance !== "undefined" ? performance.now() : 0;
    r = Array.from({ length: Math.max(1, def.recipe.vars ?? 1) }, (_, v) => renderSound(id, def.recipe, v));
    renderMs += (typeof performance !== "undefined" ? performance.now() : 0) - t;
    bank.set(id, r);
    return r;
  };
  const toBuffer = (r: Rendered): AudioBuffer => {
    const b = ctx!.createBuffer(r.channels.length, r.channels[0]!.length, r.sr);
    r.channels.forEach((c, i) => b.copyToChannel(c as Float32Array<ArrayBuffer>, i));
    return b;
  };
  const buffersOf = (id: string): AudioBuffer[] => {
    let b = buffers.get(id);
    if (b) return b;
    b = rendered(id).map(toBuffer);
    buffers.set(id, b);
    return b;
  };
  const rootsOf = (id: string): number[] => {
    const def = o.music?.instruments[id];
    return def ? (def.roots?.length ? [...def.roots].sort((a, b) => a - b) : [def.ref]) : [];
  };
  /** Render one root of an instrument (a sustained one gets its loop seam). */
  const renderRoot = (id: string, root: number): AudioBuffer | null => {
    const def = o.music?.instruments[id];
    if (!def || !ctx) return null;
    let m = instBuffers.get(id);
    if (!m) {
      m = new Map();
      instBuffers.set(id, m);
    }
    let b = m.get(root);
    if (b) return b;
    const t = typeof performance !== "undefined" ? performance.now() : 0;
    const r = renderSound(`inst:${id}`, def.recipe, 0, root - def.ref, !!def.sustain);
    if (def.sustain) for (const x of r.channels) instLoops.set(id, sustainLoop(x, r.sr, def.sustain[0], def.sustain[1]));
    b = toBuffer(r);
    instMs += (typeof performance !== "undefined" ? performance.now() : 0) - t;
    instSamples++;
    m.set(root, b);
    return b;
  };
  const instTodo = (): string[] => {
    const out: string[] = [];
    for (const id of Object.keys(o.music?.instruments ?? {})) for (const r of rootsOf(id)) if (!instBuffers.get(id)?.has(r)) out.push(`inst:${id}@${r}`);
    return out;
  };
  /** The buffer for a note: the nearest root (the planted defect: always the first), its rate and its loop. */
  const instrument = (id: string, midi: number): { buf: AudioBuffer; root: number; loop: [number, number] | null; def: Instrument } | null => {
    const def = o.music?.instruments[id];
    if (!def || !ctx) return null;
    const roots = rootsOf(id);
    const root = INSTRUMENT_FAULTS.firstRootOnly ? roots[0]! : roots.reduce((a, r) => (Math.abs(r - midi) < Math.abs(a - midi) ? r : a));
    const buf = renderRoot(id, root);
    return buf ? { buf, root, loop: instLoops.get(id) ?? null, def } : null;
  };

  function emitCaption(cue: string, def: SoundDef, pos: Vec3 | null | undefined, at: number, important: boolean): CaptionEvent | null {
    if (!important) return null;
    importantPlays[cue] = (importantPlays[cue] ?? 0) + 1;
    // the planted defect's shape: an important cue with no caption sends nothing (the count check catches it)
    if (!def.caption) {
      uncaptioned[cue] = (uncaptioned[cue] ?? 0) + 1;
      return null;
    }
    const c: CaptionEvent = { cue, caption: def.caption, direction: pos ? directionOf(pos, lis.pos, lis.fwd) : null, at };
    captions[cue] = (captions[cue] ?? 0) + 1;
    for (const fn of captionFns) fn(c);
    return c;
  }

  const occlusion = (pos: Vec3): number => (o.occlusion ? Math.min(1, Math.max(0, o.occlusion(lis.pos, pos))) : 0);

  const spatialChain = (pos: Vec3, d: number): { filter: BiquadFilterNode; panner: PannerNode; occ: number } => {
    const c = ctx!;
    const occ = occlusion(pos);
    if (occ > 0) muffled++;
    const filter = c.createBiquadFilter();
    filter.type = "lowpass";
    const air = Math.min(Math.max(20000 * Math.exp(-d / o.pool.air), 1400), c.sampleRate * 0.45);
    filter.frequency.value = air + (Math.min(air, o.pool.muffle.cutoff) - air) * occ;
    const panner = c.createPanner();
    panner.panningModel = o.pool.hrtfWithin > 0 && d < o.pool.hrtfWithin ? "HRTF" : "equalpower";
    panner.distanceModel = "inverse";
    panner.refDistance = o.pool.refDistance;
    panner.rolloffFactor = o.pool.rolloff;
    panner.maxDistance = 1000;
    if (panner.positionX) {
      panner.positionX.value = pos[0];
      panner.positionY.value = pos[1];
      panner.positionZ.value = pos[2];
    } else panner.setPosition(pos[0], pos[1], pos[2]);
    filter.connect(panner);
    return { filter, panner, occ };
  };

  function play(cue: string, p: PlayOptions = {}): PlayResult {
    const def = o.sounds[cue];
    if (!def) return { played: false, reason: "unknown", caption: null, voice: null };
    const at = p.at ?? now();
    plays[cue] = (plays[cue] ?? 0) + 1;
    const caption = emitCaption(cue, def, p.pos, at, p.important === true || def.important === true);
    if (!ctx || !mixer) {
      dropped++;
      return { played: false, reason: "locked", caption, voice: null };
    }
    const bufs = buffersOf(cue);
    if (!bufs.length) return { played: false, reason: "unknown", caption, voice: null };
    let v = p.variant ?? Math.floor(rng() * bufs.length);
    if (p.variant === undefined && bufs.length > 1 && v === lastVariant.get(cue)) v = (v + 1) % bufs.length;
    lastVariant.set(cue, v);
    const buf = bufs[v]!;
    const rate = (p.rate ?? 1) * semis((rng() * 2 - 1) * (def.pitch ?? 0.6));
    const level = dbToGain((def.gain ?? 0) + (p.gain ?? 0) + (rng() * 2 - 1) * (def.vol ?? 1.2));
    const pos = p.pos ?? null;
    let slot: number | null = null;
    let d = 0;
    if (pos) {
      d = Math.hypot(pos[0] - lis.pos[0], pos[1] - lis.pos[1], pos[2] - lis.pos[2]);
      if (d > (def.range ?? 40)) return { played: false, reason: "range", caption, voice: null };
      const grant = pool.acquire(at, { priority: def.priority ?? 1, distance: d, end: at + buf.duration / rate });
      if (!grant) return { played: false, reason: "culled", caption, voice: null };
      slot = grant.index;
      const old = active.get(slot);
      if (old && grant.stolen) {
        // a stolen voice fades over 15 ms
        old.gain.gain.cancelScheduledValues(at);
        old.gain.gain.setTargetAtTime(0, at, 0.005);
        try {
          old.src.stop(at + 0.03);
        } catch {
          /* already stopped */
        }
      }
    }
    const c = ctx;
    const src = c.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const gain = c.createGain();
    src.connect(gain);
    if (pos) {
      const chain = spatialChain(pos, d);
      gain.gain.value = level * (1 + (o.pool.muffle.gain - 1) * chain.occ);
      gain.connect(chain.filter);
      chain.panner.connect(mixer.input[def.bus]);
      active.set(slot!, { src, gain, serial: 0 });
    } else {
      gain.gain.value = level;
      gain.connect(mixer.input[def.bus]);
    }
    src.onended = () => {
      gain.disconnect();
      if (slot !== null && active.get(slot)?.src === src) active.delete(slot);
    };
    src.start(Math.max(at, c.currentTime));
    return { played: true, caption, voice: slot };
  }

  function playNote(e: NoteEvent): void {
    if (!ctx || !mixer) return;
    const inst = instrument(e.inst, e.midi);
    if (!inst) return;
    const c = ctx;
    const at = Math.max(e.at, c.currentTime);
    const release = inst.def.release ?? 0.12;
    const src = c.createBufferSource();
    src.buffer = inst.buf;
    src.playbackRate.value = semis(e.midi - inst.root);
    if (inst.loop) {
      src.loop = true;
      src.loopStart = inst.loop[0];
      src.loopEnd = inst.loop[1];
    }
    const g = c.createGain();
    const level = e.vel * dbToGain(inst.def.gain ?? 0);
    g.gain.setValueAtTime(level, at);
    g.gain.setTargetAtTime(0, at + e.dur, release);
    let out: AudioNode = g;
    if (inst.def.spread && c.createStereoPanner) {
      const p = c.createStereoPanner();
      p.pan.value = Math.max(-0.8, Math.min(0.8, ((e.midi - 66) / 30) * inst.def.spread));
      g.connect(p);
      out = p;
    }
    src.connect(g);
    const layer = e.layer as MusicLayer;
    out.connect(mixer.layers[layer]);
    // the sends into the music's space, through the layer's sends (they fade with it)
    const sends: GainNode[] = [];
    if (mixer.space) {
      for (const [amount, into] of [[inst.def.reverb ?? mixer.space.send, mixer.sends.reverb[layer]], [inst.def.echo ?? 0, mixer.sends.echo[layer]]] as const) {
        if (!(amount > 0)) continue;
        const sg = c.createGain();
        sg.gain.value = amount;
        out.connect(sg).connect(into);
        sends.push(sg);
      }
    }
    const end = at + e.dur + release * 6;
    const h = { src, g, end, release };
    if (e.layer === "base") held.add(h);
    src.onended = () => {
      g.disconnect();
      if (out !== g) out.disconnect();
      for (const sg of sends) sg.disconnect();
      held.delete(h);
    };
    src.start(at);
    src.stop(end);
  }
  /** Release every base-layer note still sounding at `at`. */
  function cutHeld(at: number): void {
    for (const h of held) {
      if (h.end <= at) continue;
      h.g.gain.cancelScheduledValues(at);
      h.g.gain.setTargetAtTime(0, at, Math.min(h.release, 0.4));
      const end = at + Math.min(h.release, 0.4) * 6;
      if (end < h.end) {
        try {
          h.src.stop(end);
        } catch {
          /* already stopped */
        }
        h.end = end;
      }
    }
  }
  /** Build the music's space (its room rendered at the context's rate) once. */
  const buildSpace = (): void => {
    const space = o.music?.space;
    if (!space || !director || !ctx || !mixer || mixer.space) return;
    const t = typeof performance !== "undefined" ? performance.now() : 0;
    const ir = makeIR(ctx.sampleRate, space.ir);
    irMs = (typeof performance !== "undefined" ? performance.now() : 0) - t;
    mixer.musicSpace(space, ir, o.music!.table.moods.find((m) => m.id === (o.music!.mood ?? o.music!.table.moods[0]!.id))?.bpm ?? 90);
  };
  const spacePending = (): boolean => !!(o.music?.space && director && mixer && !mixer.space);
  /** Whether every instrument sample (and the room) is ready (the music waits for them under `deferLoops`). */
  const musicReady = (): boolean => !o.deferLoops || (instTodo().length === 0 && !spacePending());

  /** Loops the field gave a graph whose cue wasn't rendered yet (`deferLoops`). */
  const cold = new Set<string>();
  const buildLoop = (id: string): void => {
    const l = loops.get(id);
    if (!l || l.graph || !ctx || !mixer) return;
    if (o.deferLoops && !buffers.has(l.cue)) {
      cold.add(id);
      return;
    }
    cold.delete(id);
    const bufs = buffersOf(l.cue);
    if (!bufs.length) return;
    const d = Math.hypot(l.pos[0] - lis.pos[0], l.pos[1] - lis.pos[1], l.pos[2] - lis.pos[2]);
    const def = o.sounds[l.cue]!;
    const src = ctx.createBufferSource();
    src.buffer = bufs[0]!;
    src.loop = true;
    const gain = ctx.createGain();
    gain.gain.value = 0;
    const { filter, panner, occ } = spatialChain(l.pos, d);
    src.connect(gain).connect(filter);
    panner.connect(mixer.input[def.bus]);
    const t = now();
    gain.gain.setTargetAtTime(dbToGain((def.gain ?? 0) + l.gain) * (1 + (o.pool.muffle.gain - 1) * occ), t, 0.1);
    src.start(t, rng() * bufs[0]!.duration);
    l.graph = { src, gain, filter, panner };
  };
  const releaseLoop = (id: string): void => {
    const l = loops.get(id);
    if (!l?.graph) return;
    const g = l.graph;
    const t = now();
    g.gain.gain.cancelScheduledValues(t);
    g.gain.gain.setTargetAtTime(0, t, 0.1);
    g.src.stop(t + 0.5);
    g.src.onended = () => g.gain.disconnect();
    l.graph = null;
  };

  const applyBeds = (): void => {
    if (!o.ambience) return;
    bedLevels = bedTargets(o.ambience.beds, amb);
    if (!ctx || !mixer) return;
    const t = now();
    for (const b of o.ambience.beds) {
      const target = bedLevels[b.id] ?? 0;
      let node = beds.get(b.id);
      if (!node && target <= 0) continue;
      if (!node) {
        if (o.deferLoops && !buffers.has(b.cue)) continue;
        const bufs = buffersOf(b.cue);
        if (!bufs.length) continue;
        const src = ctx.createBufferSource();
        src.buffer = bufs[0]!;
        src.loop = true;
        const gain = ctx.createGain();
        gain.gain.value = 0;
        src.connect(gain).connect(mixer.input.ambience);
        src.start(t, rng() * bufs[0]!.duration);
        node = { src, gain };
        beds.set(b.id, node);
      }
      node.gain.gain.cancelScheduledValues(t);
      node.gain.gain.setTargetAtTime(target * dbToGain(o.sounds[b.cue]?.gain ?? 0), t, o.ambience.fade);
    }
  };

  const self: Audio = {
    get context() {
      return ctx;
    },
    get mixer() {
      return mixer;
    },
    now,
    attach(c, destination) {
      if (ctx) {
        if (c !== ctx) problems.push("a second AudioContext was offered to the audio engine and refused (one context per game)");
        return c === ctx;
      }
      ctx = c;
      mixer = new Mixer(c, o.duck, o.master, destination ?? c.destination);
      for (const [bus, v] of Object.entries(volumes)) mixer.volume(bus as BusId, v!);
      analyser = c.createAnalyser();
      analyser.fftSize = 2048;
      mixer.output.connect(analyser);
      if (c.listener.positionX) c.listener.positionX.value = lis.pos[0];
      // the music's room: now, or (deferring) as the warm-up's first music unit, so the gesture's frame stays short
      if (!o.deferLoops) buildSpace();
      director?.start(now());
      applyBeds();
      return true;
    },
    play,
    listener(pos, forward) {
      lis = { pos: [pos[0], pos[1], pos[2]], fwd: [forward[0], forward[1], forward[2]] };
      if (!ctx) return;
      const L = ctx.listener;
      if (L.positionX) {
        L.positionX.value = pos[0];
        L.positionY.value = pos[1];
        L.positionZ.value = pos[2];
        L.forwardX.value = forward[0];
        L.forwardY.value = forward[1];
        L.forwardZ.value = forward[2];
        L.upX.value = 0;
        L.upY.value = 1;
        L.upZ.value = 0;
      } else {
        L.setPosition(pos[0], pos[1], pos[2]);
        L.setOrientation(forward[0], forward[1], forward[2], 0, 1, 0);
      }
    },
    loop(id, cue, pos, lo = {}) {
      const def = o.sounds[cue];
      if (!def) throw new Error(`audio: no cue "${cue}"`);
      const range = lo.range ?? def.range ?? 40;
      const old = loops.get(id);
      loops.set(id, { cue, pos, range, gain: lo.gain ?? 0, graph: old?.graph ?? null });
      field.set({ id, x: pos[0], y: pos[1], z: pos[2], range });
      if (old?.graph?.panner.positionX) {
        old.graph.panner.positionX.value = pos[0];
        old.graph.panner.positionY.value = pos[1];
        old.graph.panner.positionZ.value = pos[2];
      }
    },
    removeLoop(id) {
      releaseLoop(id);
      loops.delete(id);
      field.remove(id);
    },
    ambience(s) {
      const next = { ...amb, ...s };
      const changed = next.area !== amb.area || next.time !== amb.time || Math.abs(next.rain - amb.rain) > 0.02;
      amb = next;
      if (changed) applyBeds();
    },
    music: {
      mood: (id) => director?.mood(id, now()),
      alert: (on) => director?.alert(on, now()),
      victory: () => director?.victory(now()),
      director: () => director,
      note: (e) => playNote({ ...e, layer: e.layer ?? "sting" }),
    },
    say(line) {
      const voice = o.voices?.[line.voice ?? line.speaker] ?? o.voices?.[Object.keys(o.voices ?? {})[0] ?? ""];
      if (!voice) return { syllables: 0, seconds: 0 };
      const plan = babblePlan(line.text, voice, line.seconds);
      babble = { lines: babble.lines + 1, syllables: babble.syllables + plan.syllables.length };
      if (!ctx || !mixer) return { syllables: plan.syllables.length, seconds: plan.seconds };
      const t = now();
      const vid = line.voice ?? line.speaker;
      mixer.duckFor(t, t + plan.seconds + 0.25);
      const out = line.pos ? spatialChain(line.pos, Math.hypot(line.pos[0] - lis.pos[0], line.pos[1] - lis.pos[1], line.pos[2] - lis.pos[2])) : null;
      if (out) out.panner.connect(mixer.input.voice);
      for (const s of plan.syllables) {
        const key = `${vid}:${s.vowel}`;
        let b = syllables.get(key);
        if (!b) {
          const x = renderSyllable(vid, voice, s.vowel);
          b = ctx.createBuffer(1, x.length, 22050);
          b.copyToChannel(x as Float32Array<ArrayBuffer>, 0);
          syllables.set(key, b);
        }
        const src = ctx.createBufferSource();
        src.buffer = b;
        src.playbackRate.value = s.rate;
        const g = ctx.createGain();
        g.gain.value = s.gain * dbToGain(o.babbleGain ?? 0);
        src.connect(g).connect(out ? out.filter : mixer.input.voice);
        src.onended = () => g.disconnect();
        src.start(t + s.at);
      }
      return { syllables: plan.syllables.length, seconds: plan.seconds };
    },
    duck(seconds) {
      if (!mixer) return;
      const t = now();
      mixer.duckFor(t, t + seconds);
    },
    volume(bus, v) {
      volumes[bus] = v;
      mixer?.volume(bus, v);
    },
    onCaption(fn) {
      captionFns.add(fn);
      return () => captionFns.delete(fn);
    },
    update() {
      const t = now();
      if (ctx && director && musicReady()) director.update(t, o.music?.lookahead ?? 0.5);
      // loops: re-decided every 0.25 s
      if (t - lastCull >= 0.25 || t < lastCull) {
        lastCull = t;
        const ch = field.update(lis.pos[0], lis.pos[1], lis.pos[2]);
        for (const id of ch.release) releaseLoop(id);
        for (const id of ch.build) buildLoop(id);
      }
      if (spots) {
        for (const e of spots.update(t, amb)) {
          const pos: Vec3 = [lis.pos[0] + Math.sin(e.bearing) * e.distance, lis.pos[1] + 2, lis.pos[2] + Math.cos(e.bearing) * e.distance];
          play(e.cue, { pos, at: Math.max(e.at, t) });
        }
      }
    },
    warmStep(budgetMs, order = []) {
      const clockMs = (): number => (typeof performance !== "undefined" ? performance.now() : Date.now());
      const t = clockMs();
      // instruments: those named in `order` (inst:<id>) where they're named, the rest after the named cues; only with
      // a context (a buffer needs one)
      const insts = ctx ? [...(spacePending() ? ["inst:@space"] : []), ...instTodo()] : [];
      const named = order.filter((id) => o.sounds[id] || id.startsWith("inst:"));
      const seq: string[] = [];
      for (const id of named) {
        if (id.startsWith("inst:")) seq.push(...insts.filter((x) => x === "inst:@space" || x.startsWith(`${id}@`)));
        else seq.push(id);
      }
      seq.push(...insts);
      seq.push(...Object.keys(o.sounds));
      const todo = [...new Set(seq)].filter((id) => (id.startsWith("inst:") ? insts.includes(id) : !(ctx ? buffers.has(id) : bank.has(id))));
      let done = 0;
      for (const id of todo) {
        if (done > 0 && clockMs() - t >= budgetMs) break;
        if (id === "inst:@space") buildSpace();
        else if (id.startsWith("inst:")) {
          const [name, root] = id.slice(5).split("@");
          renderRoot(name!, Number(root));
        } else if (ctx) buffersOf(id);
        else rendered(id);
        done++;
      }
      if (done && ctx) {
        // beds and loops that were waiting for these
        applyBeds();
        for (const id of [...cold]) if (field.isBuilt(id)) buildLoop(id);
      }
      return todo.length - done;
    },
    warm(ids) {
      let n = 0;
      for (const id of ids ?? Object.keys(o.sounds)) {
        if (ctx) buffersOf(id);
        else rendered(id);
        n++;
      }
      if (!ids && ctx) buildSpace();
      if (!ids && ctx)
        for (const x of instTodo()) {
          const [name, root] = x.slice(5).split("@");
          renderRoot(name!, Number(root));
          n++;
        }
      return n;
    },
    meter() {
      if (!analyser) return 0;
      const a = new Float32Array(analyser.fftSize);
      analyser.getFloatTimeDomainData(a);
      let p = 0;
      for (let i = 0; i < a.length; i++) p = Math.max(p, Math.abs(a[i]!));
      return p;
    },
    stats() {
      const t = now();
      return {
        contexts: ctx ? 1 : 0,
        state: ctx && "state" in ctx ? String((ctx as AudioContext).state) : "none",
        plays: { ...plays },
        captions: { ...captions },
        important: { ...importantPlays },
        uncaptioned: { ...uncaptioned },
        dropped,
        voices: { size: pool.slots.length, busy: pool.busy(t), ...pool.counts },
        loops: { emitters: field.size(), built: field.builtIds().length, max: o.pool.loops, ...field.counts },
        muffled,
        ducks: mixer?.ducks ?? 0,
        babble: { ...babble },
        music: director?.state() ?? null,
        ambience: { state: { ...amb }, beds: { ...bedLevels }, spots: { ...(spots?.counts ?? {}) } },
        rendered: { cues: bank.size, ms: Math.round(renderMs), samples: instSamples, instMs: Math.round(instMs), irMs: Math.round(irMs) },
      };
    },
    problems: () => [...problems],
    dispose() {
      for (const l of loops.keys()) releaseLoop(l);
      for (const b of beds.values()) b.src.stop();
      beds.clear();
      mixer?.output.disconnect();
    },
  };
  return self;
}
